import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrashAlt,
  faHandsHelping,
  faPlus,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { Beneficiary } from '../../types/Beneficiary';
import { Assistance } from '../../types/Assistance';
import { getBeneficiary, deleteBeneficiary } from '../../services/beneficiaryService';
import { getAssistancesByBeneficiary } from '../../services/assistanceService';
import './BeneficiaryDetail.css';

const BeneficiaryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [beneficiary, setBeneficiary] = useState<Beneficiary | null>(null);
  const [assistances, setAssistances] = useState<Assistance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Cargar datos del beneficiario
        const beneficiaryData = await getBeneficiary(parseInt(id));
        setBeneficiary(beneficiaryData);
        
        // Cargar asistencias del beneficiario
        const assistancesData = await getAssistancesByBeneficiary(parseInt(id));
        setAssistances(assistancesData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching beneficiary data:', err);
        setError('No se pudo cargar la información del beneficiario. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handleDelete = async () => {
    if (!beneficiary || !beneficiary.id) return;
    
    if (window.confirm('¿Está seguro de que desea eliminar este beneficiario? Esta acción no se puede deshacer.')) {
      try {
        await deleteBeneficiary(beneficiary.id);
        navigate('/beneficiaries');
      } catch (err) {
        console.error('Error deleting beneficiary:', err);
        setError('No se pudo eliminar el beneficiario. Por favor, intente nuevamente.');
      }
    }
  };
  
  // Formatea el nombre completo del beneficiario
  const formatName = (beneficiary: Beneficiary) => {
    const { first_name, last_name, nickname } = beneficiary;
    let formattedName = [first_name, last_name].filter(Boolean).join(' ');
    if (nickname) {
      formattedName += ` '${nickname}'`;
    }
    return formattedName || 'Sin nombre';
  };
  
  // Formatea la fecha en un formato más legible (DD/MM/YYYY)
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No registrada';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };
  
  if (loading) {
    return <div className="loading-container">Cargando información del beneficiario...</div>;
  }
  
  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }
  
  if (!beneficiary) {
    return <div className="alert alert-warning">No se encontró el beneficiario.</div>;
  }
  
  return (
    <div className="beneficiary-detail-container">
      <div className="flex justify-between align-center mb-4">
        <div className="flex align-center">
          <button 
            className="btn btn-secondary mr-3"
            onClick={() => navigate('/beneficiaries')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Volver
          </button>
          <h1>Detalle del Beneficiario</h1>
        </div>
        
        <div className="action-buttons">
          <Link 
            to={`/beneficiaries/${beneficiary.id}/edit`} 
            className="btn btn-primary mr-2"
          >
            <FontAwesomeIcon icon={faEdit} /> Editar
          </Link>
          <button 
            className="btn btn-danger"
            onClick={handleDelete}
          >
            <FontAwesomeIcon icon={faTrashAlt} /> Eliminar
          </button>
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="beneficiary-header">
          <h2>{formatName(beneficiary)}</h2>
          {beneficiary.dni && (
            <div className="dni-badge">
              DNI: {beneficiary.dni}
              {beneficiary.has_physical_dni && (
                <span className="badge badge-success ml-2" title="Tiene DNI físico">
                  Físico
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="section">
          <h3>Información Personal</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Nombre:</span>
              <span className="info-value">{beneficiary.first_name || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Apellido:</span>
              <span className="info-value">{beneficiary.last_name || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Apodo:</span>
              <span className="info-value">{beneficiary.nickname || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha de Nacimiento:</span>
              <span className="info-value">{formatDate(beneficiary.birthdate)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Sexo:</span>
              <span className="info-value">{beneficiary.sex || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Nacionalidad:</span>
              <span className="info-value">{beneficiary.nationality || 'No registrada'}</span>
            </div>
          </div>
        </div>
        
        <div className="section">
          <h3>Información de Contacto</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Teléfono:</span>
              <span className="info-value">{beneficiary.phone_number || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Ref. Teléfono:</span>
              <span className="info-value">{beneficiary.reference_phone_number || 'No registrada'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Dirección:</span>
              <span className="info-value">{beneficiary.address || 'No registrada'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Ref. Dirección:</span>
              <span className="info-value">{beneficiary.address_reference || 'No registrada'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Geolocalización:</span>
              <span className="info-value">{beneficiary.geolocation || 'No registrada'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Ubicación Transitoria:</span>
              <span className="info-value">{beneficiary.is_fixed_or_transitory_place ? 'Sí' : 'No'}</span>
            </div>
          </div>
        </div>
        
        <div className="section">
          <h3>Información Adicional</h3>
          <div className="text-info-item">
            <span className="info-label">Centro de Vida:</span>
            <span className="info-value">{beneficiary.life_center || 'No registrado'}</span>
          </div>
          <div className="text-info-item">
            <span className="info-label">Contacto Familiar:</span>
            <div className="long-text-value">{beneficiary.has_family_contact || 'No registrado'}</div>
          </div>
          <div className="text-info-item">
            <span className="info-label">Subsidios:</span>
            <div className="long-text-value">{beneficiary.subsidies || 'No registrado'}</div>
          </div>
          <div className="text-info-item">
            <span className="info-label">Información de Salud:</span>
            <div className="long-text-value">{beneficiary.health_info || 'No registrado'}</div>
          </div>
        </div>
        
        <div className="section">
          <h3>Información de Registro</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Autor:</span>
              <span className="info-value">{beneficiary.author_details || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">ID Autor:</span>
              <span className="info-value">{beneficiary.author || 'No registrado'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha de Creación:</span>
              <span className="info-value">{formatDate(beneficiary.created_at || null)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Última Actualización:</span>
              <span className="info-value">{formatDate(beneficiary.updated_at || null)}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="assistances-section">
        <div className="flex justify-between align-center mb-3">
          <h2>Asistencias</h2>
          <Link 
            to={`/assistances/create?beneficiary=${beneficiary.id}`} 
            className="btn btn-primary"
          >
            <FontAwesomeIcon icon={faPlus} /> Nueva Asistencia
          </Link>
        </div>
        
        {assistances.length === 0 ? (
          <div className="card">
            <div className="no-data">
              <FontAwesomeIcon icon={faHandsHelping} size="2x" />
              <p>No hay asistencias registradas para este beneficiario.</p>
              <Link 
                to={`/assistances/create?beneficiary=${beneficiary.id}`} 
                className="btn btn-primary"
              >
                Registrar Primera Asistencia
              </Link>
            </div>
          </div>
        ) : (
          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Autor</th>
                  <th>Seguimiento</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {assistances.map((assistance) => (
                  <tr key={assistance.id}>
                    <td>{formatDate(assistance.created_at || null)}</td>
                    <td>{assistance.assistance_type || 'No especificado'}</td>
                    <td>{assistance.author}</td>
                    <td>
                      {assistance.extended_assistance ? (
                        <span className="badge badge-warning">Requiere</span>
                      ) : (
                        <span className="badge badge-info">No requiere</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/assistances/${assistance.id}`} 
                          className="icon-button" 
                          title="Ver detalles"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link 
                          to={`/assistances/${assistance.id}/edit`} 
                          className="icon-button" 
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeneficiaryDetail; 